import { useState } from 'react';
import { useNavigation } from '@/components/layout/app-layout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Warehouse,
  User,
  FileText,
  Download,
  Send,
  Save,
  FileSpreadsheet,
} from 'lucide-react';
import {
  exportStockTakeToPDF,
  exportStockTakeToExcel,
  exportStockTakeToCSV,
  StockTakeExportData,
} from '@/lib/stock-take-export-utils';
import {
  saveStockTake,
  generateStockTakeId,
  generateStockTakeReference,
  markStockTakeCompleted,
  SavedStockTake,
} from '@/lib/stock-take-storage';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface CountedItem {
  id: string;
  itemCode: string;
  description: string;
  category: string;
  systemQty: number;
  countedQty: number;
  variance: number;
  countedBy: string;
  warehouse: string;
  unit: string;
}

interface QuickCountReviewPageProps {
  setupData?: {
    countDate: string;
    warehouse: string;
    assignedTo: string;
  };
  countedItems?: CountedItem[];
}

export function StockTakesQuickCountReviewPage({ setupData, countedItems }: QuickCountReviewPageProps = {}) {
  const { navigateTo } = useNavigation();
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [stockTakeId] = useState(generateStockTakeId());
  const [reference] = useState(generateStockTakeReference());

  // Get data from previous step (in real app, this would come from state management)
  const finalSetupData = setupData || {
    countDate: '2024-01-15',
    warehouse: 'Main Warehouse',
    assignedTo: 'John Smith',
  };

  // Mock counted items data (in real app, this would come from Step 2)
  const finalCountedItems: CountedItem[] = countedItems || [
    {
      id: '1',
      itemCode: 'LAP-001',
      description: 'Dell Latitude 5420 Laptop',
      category: 'Electronics',
      systemQty: 25,
      countedQty: 23,
      variance: -2,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '2',
      itemCode: 'MON-001',
      description: 'Dell 24" Monitor',
      category: 'Electronics',
      systemQty: 40,
      countedQty: 42,
      variance: 2,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '3',
      itemCode: 'CHR-001',
      description: 'Office Chair - Ergonomic',
      category: 'Furniture',
      systemQty: 15,
      countedQty: 15,
      variance: 0,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '4',
      itemCode: 'MOU-001',
      description: 'Wireless Mouse',
      category: 'Electronics',
      systemQty: 100,
      countedQty: 95,
      variance: -5,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '5',
      itemCode: 'KEY-001',
      description: 'Mechanical Keyboard',
      category: 'Electronics',
      systemQty: 80,
      countedQty: 80,
      variance: 0,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '6',
      itemCode: 'DSK-001',
      description: 'Standing Desk',
      category: 'Furniture',
      systemQty: 12,
      countedQty: 14,
      variance: 2,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '7',
      itemCode: 'PAP-001',
      description: 'A4 Paper Ream',
      category: 'Stationery',
      systemQty: 200,
      countedQty: 198,
      variance: -2,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'reams',
    },
    {
      id: '8',
      itemCode: 'PEN-001',
      description: 'Ballpoint Pen - Blue',
      category: 'Stationery',
      systemQty: 500,
      countedQty: 500,
      variance: 0,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '9',
      itemCode: 'CAB-001',
      description: 'Filing Cabinet - 4 Drawer',
      category: 'Furniture',
      systemQty: 8,
      countedQty: 7,
      variance: -1,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'pcs',
    },
    {
      id: '10',
      itemCode: 'STL-001',
      description: 'Steel Sheets 2mm',
      category: 'Raw Materials',
      systemQty: 150,
      countedQty: 155,
      variance: 5,
      countedBy: 'John Smith',
      warehouse: 'Main Warehouse',
      unit: 'sheets',
    },
  ];

  // Calculate statistics
  const stats = {
    totalItems: finalCountedItems.length,
    itemsWithVariance: finalCountedItems.filter((item) => item.variance !== 0).length,
    itemsMatching: finalCountedItems.filter((item) => item.variance === 0).length,
    totalShortage: finalCountedItems
      .filter((item) => item.variance < 0)
      .reduce((sum, item) => sum + Math.abs(item.variance), 0),
    totalOverage: finalCountedItems
      .filter((item) => item.variance > 0)
      .reduce((sum, item) => sum + item.variance, 0),
    shortageItems: finalCountedItems.filter((item) => item.variance < 0).length,
    overageItems: finalCountedItems.filter((item) => item.variance > 0).length,
  };

  // Filter items by variance type
  const itemsWithShortage = finalCountedItems.filter((item) => item.variance < 0);
  const itemsWithOverage = finalCountedItems.filter((item) => item.variance > 0);
  const itemsMatching = finalCountedItems.filter((item) => item.variance === 0);

  const handleBack = () => {
    navigateTo('stock-takes/quick-count/count', { formData: finalSetupData });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      // Save as completed stock take
      const savedStockTake: SavedStockTake = {
        id: stockTakeId,
        reference,
        description: `Quick Count - ${new Date(finalSetupData.countDate).toLocaleDateString()}`,
        type: 'Quick Count',
        status: 'Completed',
        currentStep: 3,
        createdAt: new Date(),
        lastModified: new Date(),
        createdBy: 'Current User',
        setupData: finalSetupData,
        reviewData: {
          notes,
        },
      };
      
      saveStockTake(savedStockTake);
      
      // In real app, submit the stock take data to backend
      console.log('Submitting stock take:', {
        setupData: finalSetupData,
        countedItems: finalCountedItems,
        notes,
        stats,
      });
      
      setIsSubmitting(false);
      
      // Show success message and navigate back to stock takes list
      toast.success('Stock take completed successfully!');
      navigateTo('stock-takes');
    } catch (error) {
      setIsSubmitting(false);
      toast.error('Failed to complete stock take. Please try again.');
    }
  };

  const handleSaveAndExit = () => {
    try {
      const savedStockTake: SavedStockTake = {
        id: stockTakeId,
        reference,
        description: `Quick Count - ${new Date(finalSetupData.countDate).toLocaleDateString()}`,
        type: 'Quick Count',
        status: 'In Progress',
        currentStep: 3,
        createdAt: new Date(),
        lastModified: new Date(),
        createdBy: 'Current User',
        setupData: finalSetupData,
        reviewData: {
          notes,
        },
      };
      
      saveStockTake(savedStockTake);
      toast.success('Stock take saved successfully! You can resume it later from the Stock Takes page.');
      navigateTo('stock-takes');
    } catch (error) {
      toast.error('Failed to save stock take. Please try again.');
    }
  };

  const prepareExportData = (): StockTakeExportData => {
    return {
      setupData: finalSetupData,
      countedItems: finalCountedItems,
      statistics: stats,
      notes,
    };
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const exportData = prepareExportData();
      await exportStockTakeToPDF(exportData, reference);
      toast.success('PDF report opened in new window');
    } catch (error) {
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportData = prepareExportData();
      exportStockTakeToExcel(exportData, reference);
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export Excel. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const exportData = prepareExportData();
      exportStockTakeToCSV(exportData, reference);
      toast.success('CSV file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const getVarianceBadge = (variance: number) => {
    if (variance === 0) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Match
        </Badge>
      );
    } else if (variance > 0) {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          <TrendingUp className="h-3 w-3 mr-1" />
          +{variance}
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <TrendingDown className="h-3 w-3 mr-1" />
          {variance}
        </Badge>
      );
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Review Stock Take</h1>
          <p className="text-muted-foreground mt-1">
            Review counts and variances before finalising
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportPDF} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} disabled={isExporting}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          <Button variant="outline" onClick={handleExportCSV} disabled={isExporting}>
            <FileText className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center justify-center space-x-4">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Setup</span>
        </div>
        <div className="w-16 h-0.5 bg-green-500"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Count</span>
        </div>
        <div className="w-16 h-0.5 bg-blue-500"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium">
            3
          </div>
          <span className="ml-2 text-sm font-medium text-blue-600">Review</span>
        </div>
      </div>

      {/* Stock Take Details */}
      <Card>
        <CardHeader>
          <CardTitle>Stock Take Details</CardTitle>
          <CardDescription>Summary of the stock take configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Count Date</p>
                <p className="text-sm text-muted-foreground">{finalSetupData.countDate}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Warehouse className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Warehouse</p>
                <p className="text-sm text-muted-foreground">
                  {finalSetupData.warehouse || 'All Warehouses'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Assigned To</p>
                <p className="text-sm text-muted-foreground">
                  {finalSetupData.assignedTo || 'Unassigned'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Items Counted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalItems}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Items Matching
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.itemsMatching}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {((stats.itemsMatching / stats.totalItems) * 100).toFixed(1)}% accuracy
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Shortage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{stats.totalShortage}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.shortageItems} items affected
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Overage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.totalOverage}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.overageItems} items affected
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Variance Alert */}
      {stats.itemsWithVariance > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{stats.itemsWithVariance} items</strong> have variances that need attention.
            Review the details below before finalising the stock take.
          </AlertDescription>
        </Alert>
      )}

      {/* Detailed Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Count Details</CardTitle>
          <CardDescription>
            Review all counted items and their variances
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all">
                All Items ({stats.totalItems})
              </TabsTrigger>
              <TabsTrigger value="matching">
                Matching ({stats.itemsMatching})
              </TabsTrigger>
              <TabsTrigger value="shortage">
                Shortage ({stats.shortageItems})
              </TabsTrigger>
              <TabsTrigger value="overage">
                Overage ({stats.overageItems})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">System Qty</TableHead>
                        <TableHead className="text-right">Counted Qty</TableHead>
                        <TableHead className="text-right">Variance</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {finalCountedItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.itemCode}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.category}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {item.systemQty} {item.unit}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {item.countedQty} {item.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.variance !== 0 ? (
                              <span
                                className={
                                  item.variance > 0 ? 'text-blue-600' : 'text-red-600'
                                }
                              >
                                {item.variance > 0 ? '+' : ''}
                                {item.variance}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell>{getVarianceBadge(item.variance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="matching" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemsMatching.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No matching items
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsMatching.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.itemCode}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.category}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {item.countedQty} {item.unit}
                            </TableCell>
                            <TableCell>{getVarianceBadge(item.variance)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="shortage" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">System Qty</TableHead>
                        <TableHead className="text-right">Counted Qty</TableHead>
                        <TableHead className="text-right">Shortage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemsWithShortage.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No shortage items
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsWithShortage.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.itemCode}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.category}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {item.systemQty} {item.unit}
                            </TableCell>
                            <TableCell className="text-right">
                              {item.countedQty} {item.unit}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-red-600 font-medium">
                                {item.variance}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="overage" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">System Qty</TableHead>
                        <TableHead className="text-right">Counted Qty</TableHead>
                        <TableHead className="text-right">Overage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemsWithOverage.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No overage items
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsWithOverage.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.itemCode}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.category}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {item.systemQty} {item.unit}
                            </TableCell>
                            <TableCell className="text-right">
                              {item.countedQty} {item.unit}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-blue-600 font-medium">
                                +{item.variance}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Notes & Comments
          </CardTitle>
          <CardDescription>
            Add any additional notes or explanations for variances (optional)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="e.g., Some items were damaged and removed from inventory, Monitor overage due to recent delivery not yet recorded in system..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              These notes will be included in the stock take report
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Count
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSaveAndExit} disabled={isSubmitting} size="lg">
            <Save className="h-4 w-4 mr-2" />
            Save and Exit
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} size="lg">
            {isSubmitting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Finalise Stock Take
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
